import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageContext'
import MetaTags from '../components/MetaTags'
import Lightbox from '../components/Lightbox'
import ScrollReveal from '../components/ScrollReveal'

export default function HomePage() {
  const { lang, t, getPageUrl, getServiceUrl } = useLanguage()
  const [galleryImages, setGalleryImages] = useState([])
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [services, setServices] = useState([])

  // Hero Webform State
  const [heroForm, setHeroForm] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: 'Specialtransport',
    message: '',
    website_url: '' // Honeypot
  })
  const [heroFormStatus, setHeroFormStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [heroFormError, setHeroFormError] = useState('')

  const handleHeroFormChange = (e) => {
    const { name, value } = e.target
    setHeroForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleHeroFormSubmit = async (e) => {
    e.preventDefault()
    setHeroFormStatus('submitting')
    setHeroFormError('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: heroForm.name,
          phone: heroForm.phone,
          email: heroForm.email,
          service_type: heroForm.serviceType,
          message: heroForm.message,
          website_url: heroForm.website_url
        })
      })

      if (response.ok) {
        setHeroFormStatus('success')
        setHeroForm({
          name: '',
          phone: '',
          email: '',
          serviceType: lang === 'en' ? 'Specialized Transport' : 'Specialtransport',
          message: '',
          website_url: ''
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        setHeroFormStatus('error')
        setHeroFormError(errData.message || t.home.formErrorGeneric)
      }
    } catch (err) {
      setHeroFormStatus('error')
      setHeroFormError(t.home.formErrorConnection)
    }
  }

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setServices(data)
      })
      .catch(() => {})

    fetch('https://api.dev.auxo.dk/public/content/galleries')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const gen = data.find((g) => g.gallery_slug === 'generelt')
          if (gen && gen.images) {
            setGalleryImages(gen.images)
          } else if (data[0] && data[0].images) {
            setGalleryImages(data[0].images)
          }
        }
      })
      .catch(() => {})
  }, [])

  const defaultServicesList = [
    { slug: 'specialtransport', icon_name: 'fa-truck-ramp-box' },
    { slug: 'containerkoersel', icon_name: 'fa-boxes-stacked' },
    { slug: 'stykgods', icon_name: 'fa-boxes-stacked' },
    { slug: 'traepiller', icon_name: 'fa-wheat-awn' },
    { slug: 'national-transport', icon_name: 'fa-truck-fast' },
    { slug: 'international-transport', icon_name: 'fa-globe' },
  ]

  const heroBadgeIcons = [
    'fa-truck-front',
    'fa-map-location-dot',
    'fa-boxes-stacked',
    'fa-user-check'
  ]

  const previewGallery = galleryImages.length > 0 ? galleryImages.slice(0, 6) : [
    'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/734101346-1054615956904113-4096849147404615392-n.jpg',
    'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476816859-656735350025511-354367063843939379-n.jpg',
    'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/476448836-655303320168714-1260139362731958487-n.jpg',
    'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470160342-1004853748354071-1772744319645786737-n.jpg',
    'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470094745-1004853755020737-7287025073737737011-n.jpg',
    'https://assets.auxodev.dk/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg',
  ]

  return (
    <div className="bg-slate-50 min-h-screen">
      <MetaTags
        title={t.home.metaTitle}
        description={t.home.metaDesc}
      />

      {/* Hero Section */}
      <section className="relative min-h-[600px] w-full bg-slate-950 flex items-center overflow-hidden">
        {/* Background Image with Dark Tint Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=80,format=auto/assets/ed865070-334e-4849-904d-48dc93d1adc7/734101346-1054615956904113-4096849147404615392-n.jpg" 
            alt="M.Rou Transport flåde" 
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070d18]/95 via-[#070d18]/85 to-transparent"></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
          
          {/* Left Column: Copy & Value Proposition (7 cols) */}
          <div className="lg:col-span-7 space-y-6">
            <h1 className="text-4xl md:text-5xl font-black italic uppercase tracking-tight text-white leading-none">
              {t.home.heroTitle} <br />
              <span className="text-sky-400 font-black italic">{t.home.heroTitleHighlight}</span> {t.home.heroTitleEnd}
            </h1>
            
            <p className="text-slate-300 max-w-xl text-sm md:text-base leading-relaxed">
              {t.home.heroSubtitle}
            </p>

            {/* Bullet Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-sm text-white font-medium">
              {t.home.heroBadges.map((badge, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-sky-400 font-black shrink-0 text-sm"></i>
                  <span>{badge}</span>
                </div>
              ))}
            </div>

            {/* CTA Action Group */}
            <div className="flex flex-wrap items-center gap-3 pt-4">
              <a
                href="tel:+4525303016"
                className="bg-sky-500 hover:bg-sky-600 text-white font-bold px-6 py-3 flex items-center justify-center gap-2 text-sm transition rounded-none shadow-sm"
              >
                <i className="fa-solid fa-phone text-xs"></i>
                <span>+45 25 30 30 16</span>
              </a>
              <Link
                to={getPageUrl('contact')}
                className="bg-slate-800/80 hover:bg-slate-800 border border-slate-600/70 text-white font-medium px-6 py-3 flex items-center gap-2 text-sm transition rounded-none"
              >
                <span>{lang === 'da' ? 'Send os en besked' : 'Send us a message'}</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>

          {/* Right Column: Lead Form Card (5 cols) */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="bg-white p-6 md:p-8 rounded-none shadow-2xl max-w-md w-full text-slate-900">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-lg font-black italic uppercase text-slate-900 tracking-tight">
                    {t.home.formCardTitle}
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t.home.formCardSubtitle}
                  </p>
                </div>
                <i className="fa-regular fa-paper-plane text-sky-500 text-lg shrink-0 mt-0.5"></i>
              </div>

              {heroFormStatus === 'success' ? (
                <div className="p-6 bg-emerald-50 border border-emerald-200 text-emerald-800 text-center space-y-3">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <h3 className="font-bold text-base text-emerald-900">{t.home.formSuccessTitle}</h3>
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    {t.home.formSuccessDesc}
                  </p>
                  <button
                    type="button"
                    onClick={() => setHeroFormStatus('idle')}
                    className="mt-2 text-xs font-bold text-sky-600 hover:text-sky-700 underline"
                  >
                    {t.home.formNewMessage}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleHeroFormSubmit} className="space-y-3.5">
                  {/* Antispam honeypot field */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="hero_website_url">Website</label>
                    <input
                      type="text"
                      id="hero_website_url"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                      value={heroForm.website_url}
                      onChange={handleHeroFormChange}
                    />
                  </div>

                  <div>
                    <label htmlFor="hero_name" className="block text-xs font-bold text-slate-800 mb-1">
                      {t.home.formName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="hero_name"
                      name="name"
                      required
                      placeholder={t.home.formNamePlaceholder}
                      value={heroForm.name}
                      onChange={handleHeroFormChange}
                      className="w-full border border-slate-300 rounded-none px-3 py-2 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="hero_phone" className="block text-xs font-bold text-slate-800 mb-1">
                        {t.home.formPhone} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="hero_phone"
                        name="phone"
                        required
                        placeholder={t.home.formPhonePlaceholder}
                        value={heroForm.phone}
                        onChange={handleHeroFormChange}
                        className="w-full border border-slate-300 rounded-none px-3 py-2 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="hero_email" className="block text-xs font-bold text-slate-800 mb-1">
                        {t.home.formEmail} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="hero_email"
                        name="email"
                        required
                        placeholder={t.home.formEmailPlaceholder}
                        value={heroForm.email}
                        onChange={handleHeroFormChange}
                        className="w-full border border-slate-300 rounded-none px-3 py-2 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="hero_service" className="block text-xs font-bold text-slate-800 mb-1">
                      {t.home.formServiceType}
                    </label>
                    <select
                      id="hero_service"
                      name="serviceType"
                      value={heroForm.serviceType}
                      onChange={handleHeroFormChange}
                      className="w-full border border-slate-300 rounded-none px-3 py-2 text-xs md:text-sm text-slate-800 bg-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all"
                    >
                      {defaultServicesList.map((srv) => (
                        <option key={srv.slug} value={t.servicesList[srv.slug]?.title || srv.slug}>
                          {t.servicesList[srv.slug]?.title || srv.slug}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="hero_message" className="block text-xs font-bold text-slate-800 mb-1">
                      {t.home.formMessage} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="hero_message"
                      name="message"
                      required
                      rows={3}
                      placeholder={t.home.formMessagePlaceholder}
                      value={heroForm.message}
                      onChange={handleHeroFormChange}
                      className="w-full border border-slate-300 rounded-none px-3 py-2 text-xs md:text-sm text-slate-800 placeholder-slate-400 resize-none focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all"
                    />
                  </div>

                  {heroFormStatus === 'error' && (
                    <div className="p-3 rounded-none bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                      <i className="fa-solid fa-circle-exclamation text-rose-500 text-base shrink-0"></i>
                      <span>{heroFormError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={heroFormStatus === 'submitting'}
                    className="w-full bg-sky-500 hover:bg-sky-600 active:bg-sky-700 disabled:bg-slate-400 text-white font-bold py-3 px-4 text-xs uppercase tracking-wide flex items-center justify-center gap-2 rounded-none transition-colors cursor-pointer"
                  >
                    {heroFormStatus === 'submitting' ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin"></i>
                        <span>{t.home.formSubmitting}</span>
                      </>
                    ) : (
                      <>
                        <span>{t.home.formSubmit}</span>
                        <i className="fa-regular fa-paper-plane text-xs"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          </div>
        </section>

      {/* Transportløsninger Section */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal variant="up" className="text-left max-w-3xl mb-12">
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            {t.home.servicesSectionTitle}
          </h2>
          <p className="mt-4 text-slate-600 text-base">
            {t.home.servicesSectionSubtitle}
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {defaultServicesList.map((srv, idx) => {
            const item = t.servicesList[srv.slug] || {}
            const servicePath = getServiceUrl(srv.slug)

            return (
              <ScrollReveal key={srv.slug} variant="up" delay={idx * 100}>
                <Link
                  to={servicePath}
                  className="bg-white rounded-none p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all group flex flex-col justify-between h-full"
                >
                  <div>
                    <div className="text-sky-600 text-3xl mb-4 group-hover:text-sky-700 transition-colors">
                      <i className={`fa-solid ${srv.icon_name || 'fa-truck'}`}></i>
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors">
                      {item.title}
                    </h3>

                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                      {item.shortDesc}
                    </p>
                  </div>

                  <div className="flex items-center text-sky-600 font-bold text-sm gap-2 group-hover:translate-x-1 transition-transform">
                    <span>{t.home.readMoreAbout} {item.title}</span>
                    <i className="fa-solid fa-arrow-right text-xs"></i>
                  </div>
                </Link>
              </ScrollReveal>
            )
          })}
        </div>
      </section>

      {/* Specialtransport Spotlight Section */}
      <section className="bg-slate-100 text-slate-900 py-20 border-y border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal variant="left">
              <h2 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight mb-6">
                {t.home.spotlightTitle}
              </h2>

              <p className="text-slate-700 text-base leading-relaxed mb-6">
                {t.home.spotlightText1}
              </p>

              <p className="text-slate-700 text-base leading-relaxed mb-8">
                {t.home.spotlightText2}
              </p>

              <Link
                to={getServiceUrl('specialtransport')}
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold text-base px-6 py-3.5 rounded-none shadow-md hover:shadow-lg hover:-translate-y-1 active:translate-y-0 transition-all"
              >
                <span>{t.home.spotlightCta}</span>
                <i className="fa-solid fa-arrow-right text-sm"></i>
              </Link>
            </ScrollReveal>

            <ScrollReveal variant="right">
              <div className="rounded-none overflow-hidden shadow-lg border border-slate-200 bg-white">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/ed865070-334e-4849-904d-48dc93d1adc7/470065228-1004853678354078-8640809208142626719-n.jpg" 
                  alt="Specialtransport med M.Rou Transport" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* National & International Transport Section */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal variant="up">
          <div className="bg-white rounded-none border border-slate-200 shadow-sm overflow-hidden p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 order-1 lg:order-2">
                <h2 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                  {t.home.networkTitle}
                </h2>

                <p className="text-slate-600 text-base leading-relaxed">
                  {t.home.networkDesc}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div className="bg-slate-50 p-4 rounded-none border border-slate-100">
                    <div className="text-slate-900 font-bold mb-1">
                      <span>{t.home.networkNationalTitle}</span>
                    </div>
                    <p className="text-slate-600 text-xs">{t.home.networkNationalDesc}</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-none border border-slate-100">
                    <div className="text-slate-900 font-bold mb-1">
                      <span>{t.home.networkInternationalTitle}</span>
                    </div>
                    <p className="text-slate-600 text-xs">{t.home.networkInternationalDesc}</p>
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-2.5 sm:gap-4 items-start sm:items-center">
                  <Link
                    to={getServiceUrl('national-transport')}
                    className="text-sky-600 hover:text-sky-700 font-bold text-sm flex items-center gap-1.5"
                  >
                    <span>{t.home.networkNationalLink}</span>
                    <i className="fa-solid fa-chevron-right text-xs"></i>
                  </Link>
                  <span className="text-slate-300 hidden sm:inline">|</span>
                  <Link
                    to={getServiceUrl('international-transport')}
                    className="text-sky-600 hover:text-sky-700 font-bold text-sm flex items-center gap-1.5"
                  >
                    <span>{t.home.networkInternationalLink}</span>
                    <i className="fa-solid fa-chevron-right text-xs"></i>
                  </Link>
                </div>
              </div>

              <div className="relative rounded-none overflow-hidden shadow-md order-2 lg:order-1">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/ed865070-334e-4849-904d-48dc93d1adc7/476448836-655303320168714-1260139362731958487-n.jpg" 
                  alt="M.Rou Transport lastbiler" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Højt serviceniveau Section */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal variant="up" className="text-left max-w-3xl mb-12">
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
              {t.home.whyTitle}
            </h2>
            <p className="mt-4 text-slate-600 text-base">
              {t.home.whySubtitle}
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal variant="up" delay={0}>
              <div className="bg-white p-8 rounded-none border border-slate-200 shadow-sm h-full">
                <div className="text-sky-600 text-2xl mb-3">
                  <i className="fa-solid fa-sliders"></i>
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{t.home.why1Title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t.home.why1Desc}
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal variant="up" delay={150}>
              <div className="bg-white p-8 rounded-none border border-slate-200 shadow-sm h-full">
                <div className="text-sky-600 text-2xl mb-3">
                  <i className="fa-solid fa-comments"></i>
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{t.home.why2Title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t.home.why2Desc}
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal variant="up" delay={300}>
              <div className="bg-white p-8 rounded-none border border-slate-200 shadow-sm h-full">
                <div className="text-sky-600 text-2xl mb-3">
                  <i className="fa-solid fa-shield-halved"></i>
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{t.home.why3Title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t.home.why3Desc}
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Galleri Preview Section */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal variant="up">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className="text-xl sm:text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
                {t.home.gallerySectionTitle}
              </h2>
            </div>

            <Link
              to={getPageUrl('gallery')}
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-bold text-sm"
            >
              <span>{t.home.gallerySeeMore}</span>
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {previewGallery.map((img, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setLightboxIndex(idx)
                  setIsLightboxOpen(true)
                }}
                className="aspect-square bg-slate-100 rounded-none overflow-hidden group border border-slate-200 cursor-pointer"
              >
                <img 
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`} 
                  alt={`M.Rou Transport billede ${idx + 1}`} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </button>
            ))}
          </div>
        </ScrollReveal>
      </section>

      {/* CTA Bottom Banner */}
      <section className="bg-gradient-to-r from-slate-900 via-sky-950 to-slate-900 text-white py-16 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight max-w-2xl mx-auto">
            {t.home.ctaTitle}
          </h2>

          <p className="text-slate-300 text-base max-w-xl mx-auto">
            {t.home.ctaSubtitle}
          </p>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+4525303016"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white font-bold py-3.5 px-8 rounded-none shadow-lg hover:shadow-sky-500/25 transition-all text-sm flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sm"></i>
              <span>+45 25 30 30 16</span>
            </a>

            <Link
              to={getPageUrl('contact')}
              className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold py-3.5 px-8 rounded-none transition-colors text-sm flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope text-sm"></i>
              <span>{t.home.ctaSendMsg}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <Lightbox
        images={previewGallery}
        currentIndex={lightboxIndex}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : previewGallery.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < previewGallery.length - 1 ? prev + 1 : 0))}
      />
    </div>
  )
}
